import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { fromJS } from "immutable";
import { CircleX, Wallpaper } from "lucide-react";
import { useTranslation } from "react-i18next";
import AIContentAction from "../../actions/AIContentAction";
import ImageView from "./ImageView";
import Canva from "../../icons/Canva";
import CanvaDesign from "./CanvaDesign";
import OctagonAlertIcon from "../../icons/OctagonAlertIcon";
import ConfirmDialog from "../../dialog/ConfirmDialog";
import TriangleAlertIcon from "../../icons/TriangleAlertIcon";
import { deleteFileLogActivity, uploadFileLogActivity } from "../../api/ProjectApi";
import LayoutAction from "../../actions/LayoutAction";
import ImageUpload from "./ImageUpload";

interface ImageUploadFormProps {
  onChange: (type: string, fileId: string, file: any) => void;
}

const ImageUploadForm = ({ onChange }: ImageUploadFormProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();

  const fileImages = useSelector((state: any) => state.aiContent.get("fileImages")) || fromJS([]);

  const [selectedImageId, setSelectedImageId] = useState("");
  const [activeTab, setActiveTab] = useState<string>("host");
  const [showConfirm, setShowConfirm] = useState<any>({
    show: false,
    title: "",
    description: "",
  });

  useEffect(() => {
    if (projectId) {
      dispatch(AIContentAction.getFileImages(projectId));
      dispatch(AIContentAction.getCanvaDesigns());
    }
  }, [projectId, dispatch]);

  useEffect(() => {
    if (activeTab === "host" && fileImages.size > 0 && !selectedImageId) {
      const fileImage = fileImages.first();
      const id = fileImage.getIn(["event", "data", "fileName"]) || "";
      setSelectedImageId(id);
      onChange("HOST", id, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, fileImages, selectedImageId]);

  const handleDelete = (id) => {
    setShowConfirm({
      show: true,
      title: t("Delete image"),
      description: t("Are you sure you want to delete this image?"),
      alertIcon: <TriangleAlertIcon />,
      dangerBtn: true,
      type: "DELETE",
      data: id,
    });
  };

  const handleSubmitConfirm = () => {
    if (showConfirm.type === "DELETE") {
      deleteFileLogActivity(showConfirm.data)
        .then(() => {
          dispatch(AIContentAction.getFileImages(projectId));
          dispatch(
            LayoutAction.setNotification({
              title: t("Delete image"),
              type: "success",
              message: t("Delete image successfully!"),
            })
          );
        })
        .catch((error) => {
          if (error?.status === 409) {
            dispatch(
              LayoutAction.setNotification({
                title: t("Delete image"),
                type: "warning",
                message: t(
                  "The image is currently in use. Please remove it from all posts before deleting."
                ),
              })
            );
          } else {
            dispatch(
              LayoutAction.setNotification({
                title: t("Delete image"),
                type: "error",
                message: t("Failed to delete image."),
              })
            );
          }
        });
    }
    setShowConfirm({ show: false, title: "", description: "" });
  };

  const renderHostImage = () => {
    return (
      <div className="flex flex-col items-center justify-center">
        <ImageUpload
          selectedImageId={selectedImageId}
          setSelectedImageId={setSelectedImageId}
          onChange={(fileName, file) => {
            if (fileName && file) {
              onChange("HOST", fileName, file);
            } else {
              if (fileImages.size > 0) {
                const fileImage = fileImages.first();
                setSelectedImageId(fileImage.getIn(["event", "data", "fileName"]));
                onChange("HOST", fileImage.getIn(["event", "data", "fileName"]), null);
              } else {
                setSelectedImageId("");
                onChange("HOST", "", null);
              }
            }
          }}
        />
        {fileImages.size > 0 ? (
          <div className="w-full max-h-60 overflow-y-auto">
            <div className="grid grid-cols-5 gap-4 p-4">
              {fileImages.map((item) => (
                <div
                  key={item.get("id")}
                  className={`relative group w-20 h-20 aspect-square overflow-hidden rounded-xl cursor-pointer 
                    ${selectedImageId === item.getIn(["event", "data", "fileName"]) ? "border-2 border-blue-500 shadow-md shadow-blue-500" : "border border-gray-200"}`}
                  onClick={() => {
                    setSelectedImageId(item.getIn(["event", "data", "fileName"]));
                    onChange("HOST", item.getIn(["event", "data", "fileName"]), null);
                  }}
                >
                  <ImageView
                    fileName={item.getIn(["event", "data", "fileName"])}
                    className="w-full h-full object-contain"
                  />
                  <div className="absolute top-1 right-1">
                    <div
                      className="p-0.5 bg-red-500 rounded-md cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => handleDelete(item.get("id"))}
                    >
                      <CircleX className="w-4 h-4 text-white" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div>
      <div className="w-full mb-4 flex justify-center gap-3 relative">
        <button
          onClick={() => {
            setActiveTab("host");
            if (fileImages.size > 0) {
              const fileImage = fileImages.first();
              setSelectedImageId(fileImage.getIn(["event", "data", "fileName"]));
              onChange("HOST", fileImage.getIn(["event", "data", "fileName"]), null);
            } else {
              setSelectedImageId("");
              onChange("HOST", "", null);
            }
          }}
          className={`w-full flex items-center justify-center gap-2 px-6 py-2 text-sm font-medium z-1 
            ${activeTab === "host" ? "text-blue-600 border-b-2 border-blue-600" : "text-gray-800"}`}
        >
          <Wallpaper
            className={`w-4 h-4 ${activeTab === "host" ? "text-blue-600" : "text-gray-800"}`}
          />
          {t("Media")}
        </button>
        <button
          onClick={() => {
            setActiveTab("canva");
            setSelectedImageId("");
            onChange("HOST", "", null);
          }}
          className={`w-full flex items-center justify-center gap-2 px-6 py-2 text-sm font-medium z-1 
            ${activeTab === "canva" ? "text-blue-600 border-b-2 border-blue-600" : "text-gray-800"}`}
        >
          <Canva color={activeTab === "canva" ? "#155dfc" : "currentColor"} />
          {t("Canva")}
        </button>
        <div className="w-full absolute bottom-0 border border-gray-200"></div>
      </div>
      {activeTab === "host" ? renderHostImage() : null}
      {activeTab === "canva" ? (
        <div className="w-full h-80">
          <CanvaDesign
            projectId={projectId}
            onExportImage={async (filename, file) => {
              await uploadFileLogActivity("", file, "Shared");
              dispatch(AIContentAction.getFileImages(projectId));
              setActiveTab("host");
              setSelectedImageId(filename);
              onChange("HOST", filename, null);
            }}
            navigateToUrl={(url) => {
              navigate(url);
            }}
          />
        </div>
      ) : null}
      <ConfirmDialog
        visible={showConfirm.show}
        title={showConfirm.title}
        description={showConfirm.description}
        alertIcon={showConfirm.alertIcon || <OctagonAlertIcon />}
        dangerBtn={showConfirm.dangerBtn}
        onSubmit={handleSubmitConfirm}
        onClose={() => {
          setShowConfirm({ show: false, title: "", description: "" });
        }}
      />
    </div>
  );
};

export default ImageUploadForm;
